import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { FOCUSABLE_SELECTOR, trapFocus } from "../../utils/focusTrap";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Spinner } from "./Spinner";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type DialogTone = "default" | "danger" | "warning" | "success" | "info";

export type AlertOptions = {
  title: string;
  description?: ReactNode;
  tone?: DialogTone;
  confirmText?: string;
};

export type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  tone?: DialogTone;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
};

export type PromptOptions = {
  title: string;
  description?: ReactNode;
  tone?: DialogTone;
  confirmText?: string;
  cancelText?: string;
  defaultValue?: string;
  placeholder?: string;
  inputLabel?: string;
  inputType?: "text" | "password";
};

type DialogState = {
  type: "alert" | "confirm" | "prompt";
  options: AlertOptions | ConfirmOptions | PromptOptions;
  resolve: (value: boolean | string | null) => void;
} | null;

type DialogContextValue = {
  alert: (options: AlertOptions) => Promise<void>;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const DialogContext = createContext<DialogContextValue | null>(null);

export const useDialog = (): DialogContextValue => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return ctx;
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const toneIconMap: Record<DialogTone, string> = {
  default: "lucide:info",
  danger: "lucide:alert-triangle",
  warning: "lucide:alert-circle",
  success: "lucide:check-circle",
  info: "lucide:info",
};

const toneColorMap: Record<DialogTone, string> = {
  default: "text-primary",
  danger: "text-red-500",
  warning: "text-amber-500",
  success: "text-emerald-500",
  info: "text-sky-500",
};

const toneButtonVariant = (tone: DialogTone) => {
  if (tone === "danger") return "ghost" as const;
  return "primary" as const;
};

const toneButtonClass = (tone: DialogTone) => {
  if (tone === "danger") {
    return "bg-red-600/90 text-white hover:bg-red-700 border-red-700/50";
  }
  return "";
};

// ─────────────────────────────────────────────────────────────────────────────
// Dialog overlay component
// ─────────────────────────────────────────────────────────────────────────────

type DialogOverlayProps = {
  state: DialogState;
  onClose: (result: boolean | string | null) => void;
  loading?: boolean;
};

const DialogOverlay = ({ state, onClose, loading = false }: DialogOverlayProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const effectiveLoading = loading || isLoading;

  useEffect(() => {
    if (!state) return;
    if (state.type === "prompt") {
      const opts = state.options as PromptOptions;
      setInputValue(opts.defaultValue ?? "");
    }
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const timer = window.setTimeout(() => {
      if (state.type === "prompt" && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      } else {
        const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable && focusable.length > 0) {
          const last = focusable[focusable.length - 1];
          last.focus();
        }
      }
    }, 10);
    return () => window.clearTimeout(timer);
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const handleEsc = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && !effectiveLoading) {
        event.preventDefault();
        event.stopPropagation();
        if (state.type === "alert") {
          onClose(true);
        } else if (state.type === "confirm") {
          onClose(false);
        } else {
          onClose(null);
        }
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [state, onClose, effectiveLoading]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab" && containerRef.current) {
      trapFocus(containerRef.current, event);
    }
    if (event.key === "Enter" && state?.type === "prompt" && !effectiveLoading) {
      event.preventDefault();
      onClose(inputValue);
    }
  };

  const handleBackdropClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget && !effectiveLoading) {
      if (state?.type === "alert") {
        onClose(true);
      } else if (state?.type === "confirm") {
        onClose(false);
      } else {
        onClose(null);
      }
    }
  };

  if (!state) return null;

  const { type, options } = state;
  const tone = options.tone ?? "default";
  const confirmText = options.confirmText ?? (type === "alert" ? "OK" : "Confirm");
  const cancelText = "cancelText" in options ? (options.cancelText ?? "Cancel") : "Cancel";

  const iconName = toneIconMap[tone];
  const iconColor = toneColorMap[tone];

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby={options.description ? "dialog-description" : undefined}
        className={cn(
          "w-full max-w-md rounded-xl border border-border/50 bg-card shadow-xl",
          "animate-in fade-in-0 zoom-in-95 duration-200"
        )}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-border/40 px-5 py-4">
          <div className={cn("mt-0.5 flex-shrink-0", iconColor)}>
            <Icon icon={iconName} className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-1">
            <h2 id="dialog-title" className="text-base font-semibold text-foreground">
              {options.title}
            </h2>
            {options.description && (
              <div id="dialog-description" className="text-sm text-muted-foreground">
                {options.description}
              </div>
            )}
          </div>
        </div>

        {/* Prompt input */}
        {type === "prompt" && (
          <div className="px-5 py-4">
            {"inputLabel" in options && options.inputLabel && (
              <label htmlFor="dialog-input" className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {options.inputLabel}
              </label>
            )}
            <input
              ref={inputRef}
              id="dialog-input"
              type={"inputType" in options && options.inputType ? options.inputType : "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={"placeholder" in options ? options.placeholder : ""}
              disabled={effectiveLoading}
              className={cn(
                "w-full rounded-md border border-border/60 bg-muted/50 px-3 py-2 text-sm text-foreground",
                "placeholder:text-muted-foreground/60",
                "focus:outline-none focus:ring-2 focus:ring-primary/70",
                "disabled:opacity-60"
              )}
            />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border/40 px-5 py-3">
          {type !== "alert" && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onClose(type === "prompt" ? null : false)}
              disabled={effectiveLoading}
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant={toneButtonVariant(tone)}
            size="sm"
            className={toneButtonClass(tone)}
            onClick={() => {
              if (type === "prompt") {
                onClose(inputValue);
              } else if (type === "confirm") {
                onClose(true);
              } else {
                onClose(true);
              }
            }}
            disabled={effectiveLoading}
          >
            {effectiveLoading && <Spinner size="4" />}
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

type DialogProviderProps = {
  children: ReactNode;
};

export const DialogProvider = ({ children }: DialogProviderProps) => {
  const [state, setState] = useState<DialogState>(null);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setState({
        type: "alert",
        options,
        resolve: () => resolve(),
      });
    });
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        type: "confirm",
        options,
        resolve: (value) => resolve(value as boolean),
      });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        type: "prompt",
        options,
        resolve: (value) => resolve(value as string | null),
      });
    });
  }, []);

  const handleClose = useCallback((result: boolean | string | null) => {
    const current = state;
    setState(null);
    if (current) {
      current.resolve(result);
    }
  }, [state]);

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      <DialogOverlay state={state} onClose={handleClose} />
    </DialogContext.Provider>
  );
};

export type { DialogContextValue, DialogProviderProps };
